import { useMemo, useState, useEffect, useRef } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  HotelMetricsWidget as HotelMetricsWidgetType,
  getColumnMetadata,
  formatMetricValue,
  getShortDayLabel,
  formatDateDisplay,
} from '../specs/hotelMetrics';
import { Panel } from '../../components/Panel';
import { Switch } from '../../components/Switch';
import { ReactComponent as ChevronDownIcon } from '../../assets/icons/chevron-down.svg';
import { ReactComponent as CheckMarkIcon } from '../../assets/icons/check-mark.svg';
import { ReactComponent as ExpandIcon } from '../../assets/icons/expand.svg';
import { HotelMetricsExpandedModal } from './HotelMetricsExpandedModal';
import { downsampleRows } from '../../helpers/chart-downsample';

export type ChartType = 'bar' | 'line';

interface HotelMetricsWidgetProps {
  data: HotelMetricsWidgetType;
  chartType?: ChartType;
}

// Brand colors matching dashboard aesthetic
// Palette derived from the Claude-Design kit tokens — `var(--d-*)`
// values inlined as hex so Recharts SVG fills resolve without runtime
// CSS-var lookups. Kept in sync with HotelMetricsExpandedModal.
const CHART_COLORS = {
  current: '#4D5B6E', // --d-accent-deep
  currentLight: '#6F7E92', // --d-accent
  stly: '#A8625C', // --d-neg
  stlyLight: '#C2837E', // 75% mix of --d-neg + white
  grid: 'rgba(28,26,22,0.09)', // --d-rule
  text: '#5C5B57', // --d-ink-muted
  border: 'rgba(28,26,22,0.09)', // --d-rule
};

/**
 * Hotel Metrics Widget - Dashboard-styled visualization
 *
 * Matches dashboard aesthetic with:
 * - Brand color palette (teal, mauve)
 * - Diagonal stripe patterns for visual distinction (bar chart)
 * - Smooth line curves for trend visualization (line chart)
 * - Custom tooltip with polished styling
 * - Panel wrapper for consistent framing
 * - Custom legend with color indicators
 *
 * @param chartType - Type of chart to render: 'bar' (default) or 'line'
 */
export function HotelMetricsWidget({ data, chartType = 'bar' }: HotelMetricsWidgetProps) {
  const defaultMetric = data.displayHints?.defaultMetric || data.columns[0].key;
  const [selectedMetric, setSelectedMetric] = useState(defaultMetric);
  const [showSTLY, setShowSTLY] = useState(data.stly.enabled);
  const [showMetricMenu, setShowMetricMenu] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowMetricMenu(false);
      }
    };

    if (showMetricMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMetricMenu]);

  const columnMetadata = useMemo(
    () => getColumnMetadata(data, selectedMetric),
    [data, selectedMetric]
  );

  const chartData = useMemo(() => {
    if (!columnMetadata) return [];

    const rows = data.current.timeSeries.map((currentPoint, index) => {
      const stlyPoint = data.stly.timeSeries?.[index];
      const currentValue = currentPoint.metrics[selectedMetric];
      const stlyValue = stlyPoint?.metrics[selectedMetric];

      const finalSTLYValue =
        data.stly.enabled &&
        stlyValue !== undefined &&
        stlyValue !== null &&
        typeof stlyValue === 'number'
          ? stlyValue
          : undefined;

      return {
        date: currentPoint.date,
        stlyDate: stlyPoint?.date,
        dayLabel: getShortDayLabel(currentPoint.dayOfWeek),
        current: currentValue ?? null,
        stly: finalSTLYValue,
      };
    });

    // The model can request any date range, so cap what we hand to Recharts.
    // Both series are sampling references so STLY extrema survive too.
    return downsampleRows(rows, ['current', 'stly'], 120);
  }, [data, selectedMetric, columnMetadata]);

  const dateToLabelMap = useMemo(
    () => new Map(chartData.map((p) => [p.date, p.dayLabel])),
    [chartData]
  );

  const currentSummary = data.current.summary[selectedMetric];
  const stlySummary = data.stly.summary?.[selectedMetric];
  const comparisonKey = `${selectedMetric}Change`;
  const comparisonDelta = data.comparison?.summary?.[comparisonKey];

  if (!columnMetadata) {
    return (
      <div className="w-full p-4 bg-surface-secondary rounded-lg border border-border-primary">
        <p className="text-callout text-content-tertiary">
          Metric "{selectedMetric}" not found in data
        </p>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="w-full p-4 bg-surface-secondary rounded-lg border border-border-primary">
        <p className="text-callout text-content-tertiary">
          No data available for this period
        </p>
      </div>
    );
  }

  const formatXAxis = (dateStr: string) => {
    return dateToLabelMap.get(dateStr) || dateStr.slice(-2);
  };

  const formatYAxis = (value: number) => {
    if (columnMetadata.dataType === 'percentage') {
      return `${value.toFixed(0)}%`;
    }
    if (columnMetadata.dataType === 'currency') {
      if (value >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
      }
      return `$${value.toFixed(0)}`;
    }
    return value.toFixed(0);
  };

  type YAxisDomain = [number, number] | [number, 'auto'];
  const getYAxisDomain = (): YAxisDomain => {
    if (columnMetadata.dataType === 'percentage') {
      return [0, 100];
    }
    return [0, 'auto'];
  };

  // Custom tooltip matching dashboard style
  interface TooltipPayload {
    payload: {
      date: string;
      stlyDate?: string;
      dayLabel: string;
      current: number | null;
      stly: number | null;
    };
    value: number;
    color: string;
    name: string;
  }

  interface CustomTooltipProps {
    active?: boolean;
    payload?: TooltipPayload[];
  }

  const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
    if (!active || !payload || payload.length === 0) return null;

    const pointData = payload[0].payload;
    const indicatorClass = chartType === 'line' ? 'rounded-full' : 'rounded-[3px]';

    return (
      <div
        className="px-3 py-2.5"
        style={{
          background: 'var(--d-surface, #FFFFFF)',
          border: '1px solid rgba(28,26,22,0.09)',
          borderRadius: 10,
          boxShadow: '0 12px 32px -12px rgba(28,27,25,0.18)',
          fontFamily: 'Geist, Inter, sans-serif',
        }}
      >
        <div
          className="mb-2"
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '-0.005em',
            color: '#1C1B19',
          }}
        >
          {pointData.dayLabel}, {formatDateDisplay(pointData.date)}
        </div>
        {payload.map((entry, index: number) => {
          if (entry.value === null) return null;

          const isSTLY = entry.name === 'STLY';
          const dateToShow = isSTLY && pointData.stlyDate ? pointData.stlyDate : pointData.date;

          return (
            <div key={index} className="flex items-center gap-2 mb-1">
              <div
                className={`w-2.5 h-2.5 ${indicatorClass}`}
                style={{ backgroundColor: entry.color }}
              />
              <span style={{ fontSize: 11.5, color: '#5C5B57' }}>
                {entry.name}:
              </span>
              <span
                style={{
                  fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  fontSize: 11.5,
                  fontWeight: 500,
                  color: '#1C1B19',
                }}
              >
                {formatMetricValue(entry.value, columnMetadata)}
              </span>
            </div>
          );
        })}
        {pointData.stly !== null && pointData.current !== null && (
          <div
            className="mt-1.5 pt-1.5"
            style={{
              borderTop: '1px solid rgba(28,26,22,0.05)',
              fontSize: 11,
              color: '#7e7b72',
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              letterSpacing: '0.02em',
            }}
          >
            Change: {formatMetricValue(pointData.current - pointData.stly, columnMetadata)}
          </div>
        )}
      </div>
    );
  };

  // Custom legend — kit cadence: ink labels, kit dots, mono spacing
  const CustomLegend = () => {
    const indicatorClass = chartType === 'line' ? 'rounded-full' : 'rounded-[2px]';

    return (
      <div className="flex items-center justify-center gap-5 mt-2">
        {showSTLY && data.stly.enabled && (
          <div className="flex items-center gap-2">
            <div
              className={`w-2.5 h-2.5 ${indicatorClass}`}
              style={{ backgroundColor: CHART_COLORS.stly }}
            />
            <span
              style={{
                fontFamily: 'Geist, Inter, sans-serif',
                fontSize: 11.5,
                fontWeight: 500,
                color: '#1C1B19',
                letterSpacing: '-0.005em',
              }}
            >
              STLY
            </span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <div
            className={`w-2.5 h-2.5 ${indicatorClass}`}
            style={{ backgroundColor: CHART_COLORS.current }}
          />
          <span
            style={{
              fontFamily: 'Geist, Inter, sans-serif',
              fontSize: 11.5,
              fontWeight: 500,
              color: '#1C1B19',
              letterSpacing: '-0.005em',
            }}
          >
            Current
          </span>
        </div>
      </div>
    );
  };

  // Shorten display names for compact UI
  const getShortDisplayName = (column: typeof data.columns[0]) => {
    const shortNames: Record<string, string> = {
      occupancy: 'Occupancy',
      revenue: 'Revenue',
      ADR: 'ADR',
      RevPAR: 'RevPAR',
      bookings: 'Bookings',
    };
    return shortNames[column.key] || column.displayName;
  };

  return (
    <Panel
      label={
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setShowMetricMenu((prev) => !prev)}
            className="rounded-lg px-3 py-1.5 flex items-center gap-2 transition-colors"
            style={{
              background: 'var(--d-surface)',
              border: '1px solid var(--d-rule)',
              fontFamily: 'var(--d-font-sans)',
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: '-0.005em',
              color: 'var(--d-ink)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--d-tint-page)';
              e.currentTarget.style.borderColor = 'var(--d-accent-soft)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--d-surface)';
              e.currentTarget.style.borderColor = 'var(--d-rule)';
            }}
          >
            <span>{getShortDisplayName(columnMetadata)}</span>
            <ChevronDownIcon
              className={`w-2.5 h-2.5 transition-transform ${
                showMetricMenu ? 'rotate-180' : ''
              }`}
              style={{ color: 'var(--d-ink-ghost)' }}
            />
          </button>

          {showMetricMenu && (
            <div
              className="absolute z-[99] left-0 mt-1 w-[160px] py-1"
              style={{
                background: 'var(--d-surface)',
                border: '1px solid var(--d-rule)',
                borderRadius: 10,
                boxShadow: '0 12px 32px -12px rgba(28,27,25,0.18)',
              }}
            >
              {data.columns.map((column) => (
                <button
                  key={column.key}
                  onClick={() => {
                    setSelectedMetric(column.key);
                    setShowMetricMenu(false);
                  }}
                  className="w-full px-3 py-2 flex justify-between items-center transition-colors"
                  style={{
                    fontFamily: 'var(--d-font-sans)',
                    fontSize: 13,
                    fontWeight: selectedMetric === column.key ? 500 : 400,
                    color: 'var(--d-ink)',
                    background: selectedMetric === column.key ? 'var(--d-cream-soft)' : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (selectedMetric !== column.key) {
                      e.currentTarget.style.background = 'var(--d-accent-tint)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedMetric !== column.key) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <span>{getShortDisplayName(column)}</span>
                  {selectedMetric === column.key && (
                    <CheckMarkIcon className="w-3 h-3" style={{ color: 'var(--d-accent-deep)' }} />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      }
      actions={
        <div className="flex items-center gap-3">
          {data.stly.enabled && data.stly.timeSeries && data.stly.timeSeries.length > 0 && (
            <div className="flex items-center gap-2">
              <Switch
                checked={showSTLY}
                onChange={(e) => setShowSTLY(e.target.checked)}
              />
              <label
                className="cursor-pointer"
                style={{
                  fontFamily: 'var(--d-font-sans)',
                  fontSize: 12,
                  color: 'var(--d-ink-muted)',
                }}
              >
                vs STLY
              </label>
            </div>
          )}
          {/* Expand button */}
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="p-1.5 rounded-md transition-colors"
            style={{ color: 'var(--d-ink-muted)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--d-tint-page)';
              e.currentTarget.style.color = 'var(--d-ink)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--d-ink-muted)';
            }}
            aria-label="Expand chart"
          >
            <ExpandIcon className="w-4 h-4" />
          </button>
        </div>
      }
      className="w-full"
      hasBorder
    >
      <div className="px-4 py-3.5 space-y-3.5">
        {/* Summary metrics — kit typography, mono for numeric values
            so STLY/Current/Change read as data, not body copy. */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* STLY */}
          {data.stly.enabled && stlySummary !== undefined ? (
            <>
              <span
                style={{
                  fontFamily: 'var(--d-font-sans)',
                  fontSize: 12.5,
                  color: 'var(--d-ink-muted)',
                }}
              >
                STLY:
              </span>
              <span
                style={{
                  fontFamily: 'var(--d-font-mono)',
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'var(--d-ink)',
                  letterSpacing: '-0.005em',
                }}
              >
                {formatMetricValue(stlySummary, columnMetadata)}
              </span>
              <span style={{ color: 'var(--d-ink-ghost)', fontSize: 12 }}>|</span>
            </>
          ) : null}

          {/* Current */}
          <span
            style={{
              fontFamily: 'var(--d-font-sans)',
              fontSize: 12.5,
              color: 'var(--d-ink-muted)',
            }}
          >
            Current:
          </span>
          <span
            style={{
              fontFamily: 'var(--d-font-mono)',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--d-ink)',
              letterSpacing: '-0.005em',
            }}
          >
            {currentSummary !== undefined
              ? formatMetricValue(currentSummary, columnMetadata)
              : 'N/A'}
          </span>

          {/* Change */}
          {comparisonDelta !== undefined && (() => {
            const deltaArrow = comparisonDelta > 0 ? '↑' : comparisonDelta < 0 ? '↓' : '';
            const deltaSign = comparisonDelta > 0 ? '+' : '';
            const deltaColor =
              comparisonDelta > 0
                ? 'var(--d-pos-text)'
                : comparisonDelta < 0
                ? 'var(--d-neg-text)'
                : 'var(--d-ink-ghost)';

            let formattedDelta: string;
            if (columnMetadata.dataType === 'percentage') {
              const absValue = Math.abs(comparisonDelta);
              const formatted = absValue < 1 && absValue > 0
                ? absValue.toFixed(1)
                : Math.round(absValue).toString();
              formattedDelta = `${formatted}%`;
            } else {
              formattedDelta = formatMetricValue(
                Math.abs(comparisonDelta),
                columnMetadata
              );
            }

            return (
              <>
                <span style={{ color: 'var(--d-ink-ghost)', fontSize: 12 }}>|</span>
                <span
                  style={{
                    fontFamily: 'var(--d-font-sans)',
                    fontSize: 12.5,
                    color: 'var(--d-ink-muted)',
                  }}
                >
                  Change:
                </span>
                <span
                  style={{
                    fontFamily: 'var(--d-font-mono)',
                    fontSize: 13,
                    fontWeight: 500,
                    color: deltaColor,
                    letterSpacing: '-0.005em',
                  }}
                >
                  {deltaArrow}
                  {deltaSign}
                  {formattedDelta}
                </span>
              </>
            );
          })()}
        </div>

        {/* Chart with Dashboard Styling - Compact.
            Left margin set to 0 (was -10) so Y-axis labels like "100%"
            don't clip on the left edge — was visible as "00%" before. */}
        <div className="w-full h-[180px] sm:h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'bar' ? (
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 8, left: 0, bottom: 0 }}
              >
                {/* SVG Patterns for diagonal stripes - dashboard style */}
                <defs>
                  <pattern
                    id="currentStripes"
                    patternUnits="userSpaceOnUse"
                    width="4"
                    height="4"
                    patternTransform="rotate(45)"
                  >
                    <rect width="4" height="4" fill={CHART_COLORS.current} />
                    <line
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="4"
                      stroke={CHART_COLORS.currentLight}
                      strokeWidth="3"
                    />
                  </pattern>
                  <pattern
                    id="stlyStripes"
                    patternUnits="userSpaceOnUse"
                    width="4"
                    height="4"
                    patternTransform="rotate(45)"
                  >
                    <rect width="4" height="4" fill={CHART_COLORS.stly} />
                    <line
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="4"
                      stroke={CHART_COLORS.stlyLight}
                      strokeWidth="3"
                    />
                  </pattern>
                </defs>

                <CartesianGrid
                  strokeDasharray="2 4"
                  stroke={CHART_COLORS.grid}
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatXAxis}
                  tick={{
                    fill: CHART_COLORS.text,
                    fontSize: 11,
                    fontFamily: 'Geist, Inter, sans-serif',
                  }}
                  stroke={CHART_COLORS.border}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={formatYAxis}
                  tick={{
                    fill: CHART_COLORS.text,
                    fontSize: 11,
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  }}
                  stroke={CHART_COLORS.border}
                  domain={getYAxisDomain()}
                  axisLine={false}
                  tickLine={false}
                  width={44}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(77, 91, 110, 0.08)' }} />

                {/* Bars with diagonal stripe patterns (STLY first for timeline flow) */}
                {showSTLY && data.stly.enabled && (
                  <Bar
                    dataKey="stly"
                    fill="url(#stlyStripes)"
                    name="STLY"
                    radius={[4, 4, 0, 0]}
                  />
                )}
                <Bar
                  dataKey="current"
                  fill="url(#currentStripes)"
                  name="Current"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            ) : (
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="2 4"
                  stroke={CHART_COLORS.grid}
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatXAxis}
                  tick={{
                    fill: CHART_COLORS.text,
                    fontSize: 11,
                    fontFamily: 'Geist, Inter, sans-serif',
                  }}
                  stroke={CHART_COLORS.border}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={formatYAxis}
                  tick={{
                    fill: CHART_COLORS.text,
                    fontSize: 11,
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                  }}
                  stroke={CHART_COLORS.border}
                  domain={getYAxisDomain()}
                  axisLine={false}
                  tickLine={false}
                  width={44}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ stroke: CHART_COLORS.currentLight, strokeWidth: 1, strokeDasharray: '2 4' }} />

                {/* Lines (STLY first for timeline flow) */}
                {showSTLY && data.stly.enabled && (
                  <Line
                    type="monotone"
                    dataKey="stly"
                    stroke={CHART_COLORS.stly}
                    strokeWidth={2}
                    dot={{ fill: CHART_COLORS.stly, r: 3 }}
                    activeDot={{ r: 5, fill: CHART_COLORS.stlyLight }}
                    name="STLY"
                    connectNulls
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="current"
                  stroke={CHART_COLORS.current}
                  strokeWidth={2}
                  dot={{ fill: CHART_COLORS.current, r: 3 }}
                  activeDot={{ r: 5, fill: CHART_COLORS.currentLight }}
                  name="Current"
                  connectNulls
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Custom Legend */}
        <CustomLegend />

        {/* Footer metadata — kit `.d-card-meta` cadence: JetBrains
            Mono 11, 0.04em letter-spacing, ink-ghost, with a kit
            hairline top rule (--d-rule-soft instead of legacy
            border-border-secondary slate). */}
        <div
          className="pt-2"
          style={{
            borderTop: '1px solid var(--d-rule-soft)',
            fontFamily: 'var(--d-font-mono)',
            fontSize: 10.5,
            letterSpacing: '0.04em',
            color: 'var(--d-ink-ghost)',
          }}
        >
          {formatDateDisplay(data.timeDimension.startDate)} to{' '}
          {formatDateDisplay(data.timeDimension.endDate)}
          {(() => {
            const stlyCompleteness =
              data.stly.enabled && data.metadata?.stlyCompleteness
                ? data.metadata.stlyCompleteness
                : null;

            if (!stlyCompleteness) return null;

            if (stlyCompleteness.percentage === 100) {
              return <span> • Complete</span>;
            }

            const color =
              stlyCompleteness.percentage < 50
                ? 'var(--d-neg-text)'
                : 'var(--d-warn-text)';

            return (
              <span style={{ color }}>
                {' '}• Partial ({stlyCompleteness.percentage.toFixed(0)}%)
              </span>
            );
          })()}
        </div>
      </div>

      {/* Expanded Modal View */}
      {isExpanded && (
        <HotelMetricsExpandedModal
          data={data}
          columnMetadata={columnMetadata}
          chartData={chartData}
          showSTLY={showSTLY}
          onToggleSTLY={setShowSTLY}
          onClose={() => setIsExpanded(false)}
          currentSummary={currentSummary}
          stlySummary={stlySummary}
          comparisonDelta={comparisonDelta}
          CustomTooltip={CustomTooltip}
          CustomLegend={CustomLegend}
          getShortDisplayName={getShortDisplayName}
          chartType={chartType}
        />
      )}
    </Panel>
  );
}
